import defaultMode from '@/router/app/routes/defaultMode';
import withoutRender from '@/router/app/routes/withoutRender';
import { Query, RouteConfig } from '@/router/app/types';

const SPLIT_VIEW_QUERY_PARAMETERS = new Set([
  'pathSecondary',
  'primaryMustBe',
  'makeSecondaryAsPrimary',
  'makePrimaryAsSecondary',
  'setAsPrimary',
  'isOnlyQueryUpdate',
  'notOpenSecondaryTab',
  'doReplace',
  'rawLocation',
]);

const LEGACY_REDIRECT_ORIGIN = 'https://legacy.invalid';

const withoutSplitViewQuery = (query: Query): Query =>
  Object.keys(query).reduce<Query>((result, key) => {
    if (!SPLIT_VIEW_QUERY_PARAMETERS.has(key)) {
      result[key] = query[key];
    }
    return result;
  }, {});

const decodeQueryComponent = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

type ParsedQuery = Record<string, string | null | (string | null)[]>;

const queryFromSearch = (search: string): Query | null => {
  const query: ParsedQuery = Object.create(null);
  const searchWithoutPrefix = search.replace(/^\?/, '');
  if (!searchWithoutPrefix) {
    return query as Query;
  }

  let hasUnsafePrototypeKey = false;
  searchWithoutPrefix.split('&').forEach((parameter) => {
    const parts = parameter.replace(/\+/g, ' ').split('=');
    const key = decodeQueryComponent(parts.shift() ?? '');
    const value = parts.length > 0 ? decodeQueryComponent(parts.join('=')) : null;

    if (key === '__proto__') {
      hasUnsafePrototypeKey = true;
    } else if (!Object.prototype.hasOwnProperty.call(query, key)) {
      query[key] = value;
    } else {
      const currentValue = query[key];
      if (Array.isArray(currentValue)) {
        currentValue.push(value);
      } else {
        query[key] = [currentValue, value];
      }
    }
  });

  return hasUnsafePrototypeKey ? null : (query as Query);
};

const legacyDualModeRedirect: RouteConfig = {
  path: '/dual/*',
  // TODO: Remove this compatibility redirect after legacy split-view links are no longer needed.
  redirect: (to) => {
    const pathSecondary = to.query.pathSecondary;
    if (typeof pathSecondary === 'string') {
      try {
        const path = decodeURIComponent(pathSecondary);
        const url = new URL(path, LEGACY_REDIRECT_ORIGIN);
        const isInternalPath =
          path.startsWith('/') && !path.startsWith('//') && url.origin === LEGACY_REDIRECT_ORIGIN;
        const isRecursiveDualPath = url.pathname === '/dual' || url.pathname.startsWith('/dual/');
        if (isInternalPath && !isRecursiveDualPath) {
          const query = queryFromSearch(url.search);
          if (query !== null) {
            return {
              path: url.pathname,
              query,
              hash: url.hash,
            };
          }
        }
      } catch {
        // Fall back to the primary path when the legacy query is malformed.
      }
    }

    return {
      path: `/${to.params.pathMatch ?? ''}`,
      query: withoutSplitViewQuery(to.query),
      hash: to.hash,
    };
  },
};

const routes = [legacyDualModeRedirect, defaultMode, ...withoutRender];

export default routes;
